import React, { useEffect, useState, useCallback } from 'react';
import {
  fetchAssignmentSettings, updateAssignmentSettings,
  fetchAgentsWithConfig, updateAgentConfig,
  fetchTodayAttendance, markAttendance, setMyAvailability,
  checkIn as apiCheckIn, checkOut as apiCheckOut,
} from '../services/api';
import type { AssignmentSettings, AgentWithConfig, AgentAttendance } from '../types';
import { useAuth } from '../context/AuthContext';

type Tab = 'agents' | 'attendance' | 'settings';

// ─── Source labels ────────────────────────────────────────────────────────────

const SOURCES = ['manual', 'meta', 'google', 'indiamart', 'justdial', 'whatsapp', 'website', 'inbound', 'referral', 'other'];

const sourceLabel = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Agent Config Modal ───────────────────────────────────────────────────────

interface AgentConfigModalProps {
  agent: AgentWithConfig;
  onClose: () => void;
  onSaved: (updated: AgentWithConfig) => void;
}

const AgentConfigModal: React.FC<AgentConfigModalProps> = ({ agent, onClose, onSaved }) => {
  const [isAutoAllocated, setIsAutoAllocated] = useState(agent.is_auto_allocated ?? false);
  const [dailyCap, setDailyCap]               = useState(String(agent.daily_lead_cap ?? ''));
  const [rows, setRows]                       = useState<Array<{ source: string; subcap: string; is_active: boolean }>>(
    agent.source_assignments.map(sa => ({
      source:    sa.source,
      subcap:    sa.daily_subcap != null ? String(sa.daily_subcap) : '',
      is_active: sa.is_active,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const addRow = () => {
    setRows(r => [...r, { source: '', subcap: '', is_active: true }]);
  };

  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i));

  const updateRow = (i: number, field: 'source' | 'subcap' | 'is_active', value: string | boolean) => {
    setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        is_auto_allocated: isAutoAllocated,
        daily_lead_cap: dailyCap !== '' ? parseInt(dailyCap, 10) : null,
        source_assignments: rows
          .filter(r => r.source.trim())
          .map(r => ({
            source:       r.source.trim().toLowerCase(),
            daily_subcap: r.subcap !== '' ? parseInt(r.subcap, 10) : null,
            is_active:    r.is_active,
          })),
      };
      const updated = await updateAgentConfig(agent.id, payload);
      onSaved(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Agent Config — {agent.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Auto-allocate toggle */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isAutoAllocated}
              onChange={e => setIsAutoAllocated(e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 3 }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Include in assignment pool</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                When off, this agent is skipped by round-robin and auto assignment.
                Manual assignments and existing lead ownership are not affected.
              </div>
            </div>
          </label>

          {/* Daily cap */}
          <div>
            <label className="form-label">Daily Lead Cap</label>
            <input
              type="number"
              className="form-input"
              style={{ width: 120 }}
              min={0}
              placeholder="No limit"
              value={dailyCap}
              onChange={e => setDailyCap(e.target.value)}
            />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Leave blank for no daily limit.
            </div>
          </div>

          {/* Source assignments */}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Source Assignments</div>
            {rows.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                No source overrides — agent receives leads from all sources (up to daily cap).
              </div>
            )}
            {rows.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <select
                  className="filter-select"
                  style={{ flex: 1 }}
                  value={row.source}
                  onChange={e => updateRow(i, 'source', e.target.value)}
                >
                  <option value="">— source —</option>
                  {SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
                </select>
                <input
                  type="number"
                  className="form-input"
                  style={{ width: 90 }}
                  min={0}
                  placeholder="subcap"
                  value={row.subcap}
                  onChange={e => updateRow(i, 'subcap', e.target.value)}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={row.is_active}
                    onChange={e => updateRow(i, 'is_active', e.target.checked)}
                  />
                  Active
                </label>
                <button className="row-btn row-btn-danger" onClick={() => removeRow(i)} title="Remove">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ))}
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={addRow}>
              + Add Source Rule
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Agents Tab ───────────────────────────────────────────────────────────────

const availClass: Record<string, string> = {
  available: '#22c55e', busy: '#f59e0b', away: '#6366f1', offline: '#94a3b8',
};

const AgentsTab: React.FC = () => {
  const [agents, setAgents]       = useState<AgentWithConfig[]>([]);
  const [loading, setLoading]     = useState(true);
  const [editAgent, setEditAgent] = useState<AgentWithConfig | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setAgents(await fetchAgentsWithConfig()); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (updated: AgentWithConfig) => {
    setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
    setEditAgent(null);
  };

  if (loading) return <div className="spinner">Loading…</div>;

  return (
    <>
      <div className="data-card">
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Status</th>
                <th>Auto-Allocate</th>
                <th>Daily Cap</th>
                <th>Source Rules</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr className="table-empty-row">
                  <td colSpan={6}>No agents found.</td>
                </tr>
              ) : agents.map(a => (
                <tr key={a.id}>
                  <td>
                    <div className="avatar-chip">
                      <span className="avatar-sm">
                        {a.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                      <div>
                        <div className="avatar-sm-name">{a.name}</div>
                        <div className="td-muted" style={{ fontSize: 11 }}>{a.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: availClass[a.availability_status ?? 'offline'],
                        display: 'inline-block',
                      }} />
                      {a.availability_status ?? 'offline'}
                    </span>
                  </td>
                  <td>
                    {a.is_auto_allocated
                      ? <span className="badge badge-won" style={{ fontSize: 11 }}>Yes</span>
                      : <span className="badge badge-lost" style={{ fontSize: 11 }}>No</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {a.daily_lead_cap != null ? a.daily_lead_cap : <span className="td-muted">—</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {a.source_assignments.length > 0
                      ? a.source_assignments.map(sa => `${sa.source}${sa.daily_subcap != null ? `:${sa.daily_subcap}` : ''}`).join(', ')
                      : '—'}
                  </td>
                  <td>
                    <button className="row-btn" onClick={() => setEditAgent(a)} title="Edit config">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editAgent && (
        <AgentConfigModal
          agent={editAgent}
          onClose={() => setEditAgent(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

// ─── Attendance Tab ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  present: '#22c55e', absent: '#ef4444', half_day: '#f59e0b',
};

const AttendanceTab: React.FC = () => {
  const { user: me } = useAuth();
  const [records, setRecords]       = useState<Array<AgentAttendance & { user?: { name: string } }>>([]);
  const [loading, setLoading]       = useState(true);
  const [myAvail, setMyAvail]       = useState<string>(me?.availability_status ?? 'offline');
  const [checkingIn, setCheckingIn] = useState(false);
  const [markTarget, setMarkTarget] = useState<AgentAttendance | null>(null);
  const [markStatus, setMarkStatus] = useState('present');
  const [markNotes, setMarkNotes]   = useState('');
  const [markSaving, setMarkSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRecords(await fetchTodayAttendance()); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try { await apiCheckIn(); await load(); } catch {}
    finally { setCheckingIn(false); }
  };

  const handleCheckOut = async () => {
    setCheckingIn(true);
    try { await apiCheckOut(); await load(); } catch {}
    finally { setCheckingIn(false); }
  };

  const handleAvailability = async (status: string) => {
    setMyAvail(status);
    try { await setMyAvailability(status); } catch {}
  };

  const handleMark = async () => {
    if (!markTarget) return;
    setMarkSaving(true);
    try {
      await markAttendance(markTarget.user_id, markStatus, markNotes || undefined);
      setMarkTarget(null);
      await load();
    } catch {}
    finally { setMarkSaving(false); }
  };

  const myRecord = records.find(r => r.user_id === me?.id);
  const isAdmin  = me?.role === 'tenant_admin' || me?.role === 'manager' || me?.role === 'marketing';

  const fmt = (iso?: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* My check-in/out card */}
      <div className="data-card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>My Attendance Today</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={handleCheckIn}
            disabled={checkingIn || !!myRecord?.checked_in_at}
          >
            Check In
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleCheckOut}
            disabled={checkingIn || !myRecord?.checked_in_at || !!myRecord?.checked_out_at}
          >
            Check Out
          </button>
          {myRecord?.checked_in_at && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              In: <strong>{fmt(myRecord.checked_in_at)}</strong>
              {myRecord.checked_out_at && <> · Out: <strong>{fmt(myRecord.checked_out_at)}</strong></>}
            </span>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>Availability:</span>
            <select
              className="filter-select"
              value={myAvail}
              onChange={e => handleAvailability(e.target.value)}
            >
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="away">Away</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Team board (admin/manager only) */}
      {isAdmin && (
        <div className="data-card">
          <div className="data-card-wrap">
            <table>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Status</th>
                  <th>Availability</th>
                  {loading ? null : <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
                ) : records.length === 0 ? (
                  <tr className="table-empty-row">
                    <td colSpan={6}>No attendance records yet for today.</td>
                  </tr>
                ) : records.map(r => (
                  <tr key={r.id}>
                    <td>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>
                        {(r as any).user?.name ?? `Agent #${r.user_id}`}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{fmt(r.checked_in_at)}</td>
                    <td style={{ fontSize: 13 }}>{fmt(r.checked_out_at)}</td>
                    <td>
                      {r.status ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                          fontSize: 11, fontWeight: 700,
                          background: STATUS_COLORS[r.status] + '22',
                          color: STATUS_COLORS[r.status],
                        }}>
                          {r.status.replace('_', ' ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%',
                          background: availClass[(r as any).user?.availability_status ?? 'offline'],
                          display: 'inline-block',
                        }} />
                        {(r as any).user?.availability_status ?? '—'}
                      </span>
                    </td>
                    <td>
                      <button className="row-btn" onClick={() => { setMarkTarget(r); setMarkStatus(r.status || 'present'); setMarkNotes(r.notes || ''); }} title="Mark status">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mark attendance modal */}
      {markTarget && (
        <div className="modal-overlay" onClick={() => setMarkTarget(null)}>
          <div className="modal-box" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark Attendance</h2>
              <button className="modal-close" onClick={() => setMarkTarget(null)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="form-label">Status</label>
                <select className="filter-select" style={{ width: '100%' }} value={markStatus} onChange={e => setMarkStatus(e.target.value)}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="half_day">Half Day</option>
                </select>
              </div>
              <div>
                <label className="form-label">Notes</label>
                <input className="form-input" style={{ width: '100%' }} value={markNotes} onChange={e => setMarkNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setMarkTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleMark} disabled={markSaving}>
                {markSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Settings Tab ─────────────────────────────────────────────────────────────

const SettingsTab: React.FC = () => {
  const [settings, setSettings]       = useState<AssignmentSettings | null>(null);
  const [loading, setLoading]         = useState(true);
  const [method, setMethod]           = useState<string>('manual');
  const [overflow, setOverflow]       = useState<string>('manual_hold');
  const [awayMins, setAwayMins]       = useState('30');
  const [timezone, setTimezone]       = useState('');
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    fetchAssignmentSettings()
      .then((s: AssignmentSettings) => {
        setSettings(s);
        setMethod(s.allocation_method);
        setOverflow(s.overflow_policy);
        setAwayMins(String(s.auto_away_minutes ?? 30));
        setTimezone(s.timezone ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await updateAssignmentSettings({
        allocation_method: method,
        overflow_policy:   overflow,
        auto_away_minutes: parseInt(awayMins, 10) || 30,
        timezone:          timezone || null,
      });
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="spinner">Loading…</div>;

  return (
    <div className="data-card" style={{ padding: 24, maxWidth: 560 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20 }}>Lead Assignment Settings</div>

      <div style={{ marginBottom: 18 }}>
        <label className="form-label">Allocation Method</label>
        <select className="filter-select" style={{ width: '100%' }} value={method} onChange={e => setMethod(e.target.value)}>
          <option value="manual">Manual — admins assign leads by hand</option>
          <option value="round_robin">Round Robin — rotate through agents equally</option>
          <option value="auto">Auto — source-aware, cap-aware smart assignment</option>
        </select>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Both "Round Robin" and "Auto" only assign to agents with the
          "Include in assignment pool" toggle ON (see Agents tab).
          "Auto" additionally respects daily caps, source rules, and availability status.
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label className="form-label">Overflow Policy</label>
        <select className="filter-select" style={{ width: '100%' }} value={overflow} onChange={e => setOverflow(e.target.value)}>
          <option value="manual_hold">Hold for manual review — lead stays unassigned</option>
          <option value="drop">Drop — skip the assignment silently</option>
        </select>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Applied when no eligible agent is available to receive the lead.
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label className="form-label">Auto-Away after (minutes)</label>
        <input
          type="number"
          className="form-input"
          style={{ width: 120 }}
          min={1}
          value={awayMins}
          onChange={e => setAwayMins(e.target.value)}
        />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Agents inactive for this many minutes are moved to "away" automatically.
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label className="form-label">Timezone</label>
        <input
          type="text"
          className="form-input"
          style={{ width: '100%' }}
          placeholder="Asia/Kolkata"
          value={timezone}
          onChange={e => setTimezone(e.target.value)}
        />
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          IANA timezone for daily cap resets (e.g. Asia/Kolkata). Leave blank to use UTC.
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      {saved && <div className="alert alert-success" style={{ marginBottom: 12 }}>Settings saved.</div>}

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AssignmentPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin';
  const isManager = user?.role === 'manager';

  const [tab, setTab] = useState<Tab>('attendance');

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 18px',
    border: 'none',
    background: tab === t ? 'var(--primary)' : 'transparent',
    color: tab === t ? '#fff' : 'var(--text)',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 13,
    transition: 'background 0.15s',
  });

  return (
    <div className="list-page">
      <div className="page-hd">
        <h1 className="page-title">Assignment</h1>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--surface)', padding: 4, borderRadius: 8, width: 'fit-content' }}>
        {(isAdmin || isManager) && (
          <button style={tabStyle('agents')} onClick={() => setTab('agents')}>Agents</button>
        )}
        <button style={tabStyle('attendance')} onClick={() => setTab('attendance')}>Attendance</button>
        {isAdmin && (
          <button style={tabStyle('settings')} onClick={() => setTab('settings')}>Settings</button>
        )}
      </div>

      {tab === 'agents'     && (isAdmin || isManager) && <AgentsTab />}
      {tab === 'attendance' && <AttendanceTab />}
      {tab === 'settings'   && isAdmin && <SettingsTab />}
    </div>
  );
};

export default AssignmentPage;
